import * as types from '@/store/mutation-types';
import { PAGE_TYPES } from '@/constants';
import _ from 'lodash';

const state = {
  currentPage: {
    blocks: [],
  },
  initialCurrentPage: {},
  initialDropletCounts: {
    forms: [],
  },
  previewOptions: false,
  revertTriggered: false,
  saveOptions: false,
};

const getters = {
  siteBuilt(state) {
    return state.siteBuilt;
  },
  currentPage(state) {
    return state.currentPage;
  },
  initialCurrentPage(state) {
    return state.initialCurrentPage;
  },
  initialDropletCounts(state) {
    return state.initialDropletCounts;
  },
  getContentBlocks(state) {
    return state.currentPage.blocks;
  },
  previewOptions(state) {
    return state.previewOptions;
  },
  saveOptions(state) {
    return state.saveOptions;
  },
  revertTriggered(state) {
    return state.revertTriggered;
  },
};

const mutations = {
  [types.SET_CURRENT_PAGE](state, payload) {
    this._vm.$log.debug('SET_CURRENT_PAGE: ', payload.page);
    state.currentPage = payload.page;
    state.initialCurrentPage = _.cloneDeep(payload.page);
    // set the initial droplet counts
    if (payload.site.forms) {
      state.initialDropletCounts.forms = _.cloneDeep(payload.site.forms);
    }
  },
  [types.UPDATE_INITIAL_PAGE](state, payload) {
    state.initialCurrentPage = _.cloneDeep(state.currentPage);
    // set the initial droplet counts
    if (payload.site.forms) {
      state.initialDropletCounts.forms = _.cloneDeep(payload.site.forms);
    }
    this._vm.$log.debug('UPDATE_INITIAL_PAGE: ', state.currentPage);
  },
  [types.SET_CANVAS_PAGE](state, payload) {
    state.currentPage = payload.drp;
    state.initialCurrentPage = _.cloneDeep(payload.drp);
    // set the initial droplet counts
    if (payload.site.forms) {
      state.initialDropletCounts.forms = _.cloneDeep(payload.site.forms);
    }
    this._vm.$log.debug('SET_CANVAS_PAGE: ', state.currentPage);
  },
  [types.REVERT_DROPLET_COUNTS](state, p) {
    if (p.site.forms) {
      p.site.forms = state.initialDropletCounts.forms;
    }
  },
  [types.REVERT_PAGE](state, p) {
    this._vm.$log.debug('REVERT_PAGE: ', p);
    state.currentPage = p.page;
    // TODO: only do this if not queued types? like when is there a save and publish event? no,
    // a publish shouldn't clean history
    // TODO: does reverting changes, remove form count?
    p.pages.splice(p.i, 1, p.page);
    state.revertTriggered = Date.now();
  },
  [types.UPDATE_PAGE](state, payload) {
    payload.page.url = payload.url;
    this._vm.$log.debug('UPDATE_PAGE_URL: ', payload.page);
  },
  [types.UPDATE_PAGE_LINKS](state, payload) {
    // Given that the parent chain of a page can change whenever the page changes
    // location, we have to parse the sitemap to recreate the urls for all the
    // pages to make sure we have th correct path
    const fn = (smPage, depth) => {
      // we have to make sure we reset the urls to the page name so the map doesn't
      // keep tacking on to the paths each time a user moves the map around
      let path = smPage.url;
      if (path !== '/' && path.endsWith('/')) {
        path = path.split('/').reverse()[1];
      }
      const url = `${depth}${path}/`;
      const page = _.find(payload.site.pages, p => p._id === smPage._id);
      // we only need to update the page url if it's different
      if (page.url !== url) {
        page.url = url;
        this._vm.$log.debug('UPDATE_PAGE_URL: ', page);
      }
      smPage.subPages.forEach(p => fn(p, url));
    };
    payload.sitemap.forEach(p => fn(p, ''));
  },
  [types.RECEIVE_PAGES](state, p) {
    this._vm.$log.debug('RECEIVE_PAGES', p.pages);
    p.site.pages = p.pages;
  },
  [types.RECEIVE_TEMPLATES](state, p) {
    this._vm.$log.debug('RECEIVE_TEMPLATES', p.templates);
    p.site.templates = p.templates;
  },
  [types.RECEIVE_DRAFTS](state, p) {
    this._vm.$log.debug('RECEIVE_DRAFTS', p.drafts);
    p.site.drafts = p.drafts;
  },
  [types.REMOVE_PAGE](state, p) {
    this._vm.$log.debug('REMOVE_PAGE', p);
    const site = p.site;
    const type = `${p.page.type}s`;
    const idx = _.findIndex(site[type], pg => pg._id === p.page._id);
    site[type].splice(idx, 1);
  },
  [types.REPLACE_QUEUED_PAGE](state, { site, page }) {
    const type = `${page.type}s`;
    const index = _.findIndex(site[type], item => item._id === page._id);
    if (index > -1) {
      site[type].splice(index, 1, page);
      this._vm.$log.debug('REPLACE_QUEUED_PAGE', page);
    }
  },
  [types.SET_PREVIEW_OPTIONS](state, p) {
    state.previewOptions = p;
  },
  [types.SET_SAVE_OPTIONS](state, p) {
    state.saveOptions = p;
  },
};

const actions = {
  async setCurrentPage({ commit, rootState }, payload) {
    const { currentSite } = rootState.Websites;
    // set page blank first so we can kick off the loader
    commit(types.SET_CURRENT_PAGE, {
      page: payload,
      site: currentSite,
    });
    // Update localLastPageEdited
    if (payload._id) {
      window.localStorage.setItem('lastPageEdited', payload._id);
    }
  },
  setPreviewOptions({ commit }, p) {
    commit(types.SET_PREVIEW_OPTIONS, p);
  },
  setSaveOptions({ commit }, p) {
    commit(types.SET_SAVE_OPTIONS, p);
  },
  updateInitialPage({ commit, rootState }) {
    commit(types.UPDATE_INITIAL_PAGE, {
      site: rootState.Websites.currentSite,
    });
  },
  setCanvasPage({ commit, rootState }, payload) {
    payload.site = rootState.Websites.currentSite;
    window.localStorage.setItem('lastPageEdited', payload.drp._id);
    return new Promise((resolve) => {
      commit(types.SET_CANVAS_PAGE, payload);
      resolve(payload);
    });
  },
  revertDropletCounts({ commit }, p) {
    return new Promise((resolve) => {
      commit(types.REVERT_DROPLET_COUNTS, p);
      resolve();
    });
  },
  revertPage({ commit, rootState }, payload) {
    const site = rootState.Websites.currentSite;
    let list = [];
    if (PAGE_TYPES.includes(payload.type)) {
      const types = site[`${payload.type}s`];
      list = types;
      if (payload.type === 'dynamic page') {
        Object.keys(site.dynamic).forEach((type) => {
          const pages = site.dynamic[type].pages || [];
          if (pages.find(page => page._id === payload._id)) {
            list = pages;
          }
        });
      }
    } else {
      list = site.recycled;
    }
    return new Promise((resolve) => {
      const i = _.findIndex(list, p => p._id === payload._id);
      commit(types.REVERT_PAGE, {
        page: payload,
        pages: list,
        i,
      });
      resolve();
    });
  },
  async removePage({ commit, rootState }, payload) {
    const p = {
      site: rootState.Websites.currentSite,
      page: payload,
    };
    return new Promise((resolve) => {
      commit(types.REMOVE_PAGE, p);
      resolve();
    });
  },
  async replaceQueuedPage({ commit, rootState }, payload) {
    commit(types.REPLACE_QUEUED_PAGE, {
      site: rootState.Websites.currentSite,
      page: payload,
    });
  },
  updatePageLinks({ commit, rootState }, payload) {
    const site = rootState.Websites.currentSite;
    commit(types.UPDATE_PAGE_LINKS, { sitemap: payload.sitemap, site });
  },
  async updatePage({ commit }, payload) {
    commit(types.UPDATE_PAGE, payload);
  },
};

export default {
  state,
  mutations,
  actions,
  getters,
};



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/vuex.js